import { computed, reactive, ref, watch } from "vue";
import { defineStore } from "pinia";
import { toBoolean } from "#ce/utils/props";
import { useFlash } from "#ce/composables/useFlash";
import { createAssignSpecialtiesApi } from "#ce/apps/SpAdmin/SpAssignSpecialtiesApp/api/assign-specialties";
import { tokenize } from "#ce/apps/SpAdmin/SpAssignSpecialtiesApp/utils/term-parser";
import { useRowSelection } from "#ce/apps/SpAdmin/shared/composables/useRowSelection";
import { deepMerge, parseJson } from "#ce/apps/SpAdmin/shared/utils/config";
import { isAcceptedFile } from "#ce/apps/SpAdmin/shared/utils/file-acceptance";
import { pluralizeTemplate } from "#ce/apps/SpAdmin/shared/utils/pluralize";
import { pollPreviewStatus } from "#ce/apps/SpAdmin/shared/utils/preview-poll";
import {
  formatProgressText,
  formatRemainingText,
  progressPercent,
} from "#ce/apps/SpAdmin/shared/utils/preview-progress";

function defaultConfig() {
  return {
    previewUrl: "",
    statusUrl: "",
    cancelUrl: "",
    processUrl: "",
    clientName: "",
    specialties: "[]",
    dropzoneText: "Drag and drop spreadsheet here, or <strong>click to browse</strong>",
    fileSpecHtml: "",
    termInputLabel: "Terms (separated by |)",
    modeAddLabel: "Add to existing specialties",
    modeReplaceLabel: "Replace existing specialties",
    modeLabel: "For existing terms",
    specialtySelectLabel: "Specialties to assign",
    flagToggleLabel: "Keep specialties under {client} control",
    clientControlEnabled: false,
    outcomeLabels: "",
    statusLabels: "",
    labels: "{}",
    modalTitle: "Assign Specialties",
  };
}

// Minimal English defaults so specs and Storybook work standalone; production
// always deep-merges the helper's localized `labels` JSON over these.
function labelDefaults(client) {
  return {
    preview: "Preview",
    back: "Back",
    confirm: "Assign",
    cancelPreview: "Cancel",
    viewImport: "View import",
    fileErrors: "File errors",
    orDivider: "or",
    dropzone: {
      remove: "Remove",
      viewSpec: "View file specifications",
      hideSpec: "Hide file specifications",
    },
    termInputPlaceholder: "Knee Pain | Sports Medicine",
    invalidEntry: "Invalid entry",
    removedSpecialty: "Removed: %{name}",
    termsEntered: { one: "%{count} term entered", other: "%{count} terms entered" },
    loading: "Previewing the specialty assignments…",
    progress: {
      rows: "%{processed} of %{total} rows previewed",
      remaining: "About %{time} remaining",
      minutes: { one: "%{count} minute", other: "%{count} minutes" },
      seconds: { one: "%{count} second", other: "%{count} seconds" },
    },
    columns: {
      status: "Status",
      name: "Name",
      type: "Type",
      resultingSpecialties: "Resulting specialties",
      control: "Control",
    },
    errors: {
      unsupportedFile: "Unsupported file type. Please use .csv, .xlsx, or .xls files.",
      noTerms: "Please provide terms via text or file upload.",
      noSpecialties: "Please select at least one specialty to assign.",
      previewFailed: "An error occurred while previewing. Please try again.",
      previewInterrupted: "The preview was interrupted. Please run the preview again.",
      processFailed: "An error occurred while processing. Please try again.",
    },
    summary: {
      new: { one: "%{count} new term will be requested.", other: "%{count} new terms will be requested." },
      existing: { one: "%{count} existing will be updated.", other: "%{count} existing will be updated." },
      willEnable: {
        one: `%{count} will enable Use ${client} Specialties.`,
        other: `%{count} will enable Use ${client} Specialties.`,
      },
      errors: { one: "%{count} with errors.", other: "%{count} with errors." },
    },
    selection: {
      selectAll: "Include all rows",
      includeRow: "Include %{name}",
      selectedCount: "%{count} of %{total} selected",
    },
    flash: {
      queued: {
        one: "Assigning specialties to %{count} term in the background — the results will appear shortly.",
        other: "Assigning specialties to %{count} terms in the background — the results will appear shortly.",
      },
    },
  };
}

function emptySummary() {
  return { new: 0, existing: 0, will_enable: 0, errors: 0, total: 0 };
}

export const useAssignSpecialtiesStore = defineStore("assignSpecialties", () => {
  const api = createAssignSpecialtiesApi();
  const flash = useFlash();
  // Explicit per-row approval over the preview, keyed by row name (names are
  // deduped server-side). Re-baselined on every preview response.
  const selection = useRowSelection();

  const config = reactive(defaultConfig());

  // Guards against stale async responses: an in-flight preview/confirm started
  // before reset()/close() (or superseded by a newer request) must not apply
  // its response over newer state. Non-reactive — only compared, never rendered.
  let requestGeneration = 0;

  // STATE:
  const isOpen = ref(false);
  // Bumped on every open() so per-open UI (e.g. the dropzone's local drag/spec
  // state) can be remounted fresh via a :key binding.
  const openGeneration = ref(0);
  const currentStep = ref("input");
  const inputText = ref("");
  const terms = ref([]);
  const termFile = ref(null);
  const selectedSpecialtyIds = ref([]);
  const mode = ref("add");
  const enableClientSpecialties = ref(true);
  const previewData = ref(null);
  // True from the preview request until the background preview settles.
  const isLoading = ref(false);
  // Identifies the server-side run while it can still be cancelled; "" once it settles.
  const previewToken = ref("");
  // `{ processed, total, estimatedSecondsRemaining }` while the background preview
  // reports row progress, null otherwise.
  const previewProgress = ref(null);
  const isSubmitting = ref(false);
  const errorMessage = ref("");
  const completedCount = ref(0);

  // COMPUTED:
  const clientLabel = computed(() => config.clientName || "client");
  const specialtyOptions = computed(() => parseJson(config.specialties, []));
  const flagToggleLabel = computed(() => config.flagToggleLabel.replaceAll("{client}", clientLabel.value));
  // Whether this deployment manages client-controlled specialties (canonical MST
  // management). When off, every client-control surface (toggle, Control column,
  // will-enable summary fragment) is hidden and the flag is submitted as false.
  const clientControlEnabled = computed(() => toBoolean(config.clientControlEnabled));
  const outcomeLabels = computed(() => ({
    will_enable: `Use ${clientLabel.value} Specialties`,
    unchanged: "Unchanged",
    new_term: `${clientLabel.value} (User Added)`,
    ...parseJson(config.outcomeLabels, {}),
  }));
  const statusLabels = computed(() => ({
    new: "New term, will be requested",
    existing: "Existing term, will be updated",
    ...parseJson(config.statusLabels, {}),
  }));
  // Localized UI copy from the helper, deep-merged over the English defaults.
  const labels = computed(() => deepMerge(labelDefaults(clientLabel.value), parseJson(config.labels, {})));
  // The flag the backend receives: the user's toggle only counts when the
  // deployment supports client control at all.
  const clientSpecialtiesRequested = computed(() => clientControlEnabled.value && enableClientSpecialties.value);
  const selectableRows = computed(() => (previewData.value?.rows ?? []).filter((row) => row.status !== "error"));
  // The checked subset is what confirm() sends.
  const assignableRows = computed(() => selectableRows.value.filter((row) => selection.isSelected(row.name)));
  const requestInFlight = computed(() => isLoading.value || isSubmitting.value);
  const confirmDisabled = computed(() => requestInFlight.value || assignableRows.value.length === 0);
  const selectionSummary = computed(() =>
    String(labels.value.selection.selectedCount ?? "")
      .replaceAll("%{count}", selection.selectedCount.value)
      .replaceAll("%{total}", selectableRows.value.length),
  );
  const progressPercentValue = computed(() => progressPercent(previewProgress.value));
  const progressText = computed(() => formatProgressText(previewProgress.value, labels.value.progress));
  const remainingText = computed(() =>
    formatRemainingText(previewProgress.value?.estimatedSecondsRemaining, labels.value.progress),
  );

  // Every selectable row starts approved whenever preview data arrives or clears. Sync flush so
  // the selection is consistent immediately after previewData is assigned.
  function resetSelection() {
    selection.reset(selectableRows.value.map((row) => row.name));
  }

  watch(previewData, resetSelection, { flush: "sync" });

  // ACTIONS:
  // An undefined value is an attribute the mount left unset: the default stands.
  function configure(nextConfig = {}) {
    const defaults = defaultConfig();
    const provided = Object.entries(nextConfig).filter(([key, value]) => key in defaults && value !== undefined);

    Object.assign(config, defaults, Object.fromEntries(provided));
  }

  // Asks the server to stop the background preview `token` identifies. Fire-and-forget:
  // callers abandon the poll client-side whatever the server answers.
  function cancelServerPreview(token) {
    if (!token) {
      return;
    }

    api.cancelPreview(config.cancelUrl, token).catch(() => {});
  }

  function reset() {
    cancelServerPreview(previewToken.value);
    requestGeneration += 1;
    currentStep.value = "input";
    inputText.value = "";
    terms.value = [];
    termFile.value = null;
    selectedSpecialtyIds.value = [];
    mode.value = "add";
    enableClientSpecialties.value = true;
    previewData.value = null;
    isLoading.value = false;
    previewToken.value = "";
    previewProgress.value = null;
    isSubmitting.value = false;
    errorMessage.value = "";
  }

  function open(overrides = {}) {
    Object.assign(config, overrides);
    openGeneration.value += 1;
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
    reset();
  }

  function addTerms(text) {
    const existing = new Set(terms.value.map((term) => term.toLowerCase()));

    for (const term of tokenize(text)) {
      if (!existing.has(term.toLowerCase())) {
        terms.value.push(term);
        existing.add(term.toLowerCase());
      }
    }
  }

  function removeTerm(index) {
    terms.value.splice(index, 1);
  }

  function commitInput(text = inputText.value) {
    if (!text?.trim()) {
      return;
    }

    addTerms(text);
    inputText.value = "";
  }

  function handleFileChange(file) {
    if (!file) {
      return;
    }

    if (!isAcceptedFile(file)) {
      // Also drop any previously attached valid file — keeping it while the
      // error shows would silently submit a file the user just tried to replace.
      termFile.value = null;
      errorMessage.value = labels.value.errors.unsupportedFile;
      return;
    }

    termFile.value = file;
    errorMessage.value = "";
  }

  function removeFile() {
    termFile.value = null;
  }

  function applyPreviewData(data) {
    previewData.value = {
      rows: data.rows || [],
      summary: { ...emptySummary(), ...(data.summary || {}) },
      errors: data.errors || [],
    };
    currentStep.value = "confirm";
  }

  function buildPreviewFormData() {
    const formData = new FormData();

    if (termFile.value) {
      formData.append("term_file", termFile.value);
    }

    if (terms.value.length > 0) {
      formData.append("term_text", terms.value.join("|"));
    }

    for (const id of selectedSpecialtyIds.value) {
      formData.append("specialty_ids[]", id);
    }

    formData.append("assign_mode", mode.value);
    formData.append("enable_client_specialties", clientSpecialtiesRequested.value ? "true" : "false");

    return formData;
  }

  async function preview() {
    commitInput();

    if (terms.value.length === 0 && !termFile.value) {
      errorMessage.value = labels.value.errors.noTerms;
      return false;
    }

    if (selectedSpecialtyIds.value.length === 0) {
      errorMessage.value = labels.value.errors.noSpecialties;
      return false;
    }

    cancelServerPreview(previewToken.value);
    requestGeneration += 1;
    const generation = requestGeneration;
    isLoading.value = true;
    errorMessage.value = "";
    previewToken.value = "";
    previewProgress.value = null;

    try {
      const started = await api.preview(config.previewUrl, buildPreviewFormData());

      if (generation !== requestGeneration) {
        // Superseded while the start request was in flight (a cancel, a close, or a
        // newer preview): the run the server just queued has no poller, so it is
        // cancelled too.
        cancelServerPreview(started?.token);
        return false;
      }

      // A rejected start (422) answers with the error preview's rows and summary;
      // those render without polling.
      if (Array.isArray(started?.rows)) {
        applyPreviewData(started);
        return true;
      }

      if (!started?.token) {
        errorMessage.value = labels.value.errors.previewFailed;
        return false;
      }

      previewToken.value = started.token;

      // The generation guard covers the whole poll: a response landing after
      // cancelPreview(), close(), or a newer preview is discarded, not applied.
      const result = await pollPreviewStatus({
        fetchStatus: () => api.previewStatus(config.statusUrl, started.token),
        isCurrent: () => generation === requestGeneration,
        onProgress: (progress) => {
          previewProgress.value = progress;
        },
      });

      if (result.outcome === "ready") {
        applyPreviewData(result.data);
        return true;
      }

      if (result.outcome === "stale") {
        return false;
      }

      // The run is gone server-side — its token expired, or another of this user's
      // previews cancelled it — so a fresh preview is asked for.
      if (result.outcome === "expired" || result.outcome === "cancelled") {
        errorMessage.value = labels.value.errors.previewInterrupted;
        return false;
      }

      // A failed outcome can be the poll giving up on a run the server is still
      // working on, so the run is cancelled rather than left to finish unwatched.
      cancelServerPreview(started.token);
      errorMessage.value = result.errors.join(", ") || labels.value.errors.previewFailed;
      return false;
    } catch (error) {
      if (generation !== requestGeneration) {
        return false;
      }

      cancelServerPreview(previewToken.value);
      errorMessage.value = error.message || labels.value.errors.previewFailed;
      return false;
    } finally {
      // A stale request must not clear a newer request's loading state.
      if (generation === requestGeneration) {
        isLoading.value = false;
        previewToken.value = "";
        previewProgress.value = null;
      }
    }
  }

  // Abandons the running preview, keeping the terms, file, and selection. The
  // `finally` in preview() clears state only for the generation that settles, so
  // the abandoned poll's state is cleared here.
  function cancelPreview() {
    cancelServerPreview(previewToken.value);
    requestGeneration += 1;
    isLoading.value = false;
    previewToken.value = "";
    previewProgress.value = null;
  }

  async function confirm() {
    requestGeneration += 1;
    const generation = requestGeneration;
    isSubmitting.value = true;
    errorMessage.value = "";

    try {
      const data = await api.process(config.processUrl, {
        terms: assignableRows.value.map((row) => ({ name: row.name, type: row.type })),
        specialty_ids: selectedSpecialtyIds.value,
        assign_mode: mode.value,
        enable_client_specialties: clientSpecialtiesRequested.value,
      });

      if (generation !== requestGeneration) {
        return false;
      }

      // The assignment runs in the background, so the response only acknowledges
      // the queued run. The flash links to the Bulk Import record the run fills.
      const parts = [pluralizeTemplate(labels.value.flash.queued, Number(data.total || 0))];

      if (data.errors?.length > 0) {
        parts.push(data.errors.join(". "));
      }

      const flashOptions = { autoremoveDelay: 8000 };

      if (data.bulk_import_url) {
        flashOptions.action = { href: data.bulk_import_url, label: labels.value.viewImport };
      }

      completedCount.value += 1;
      flash.success(parts.join(" "), flashOptions);
      close();

      return true;
    } catch (error) {
      if (generation !== requestGeneration) {
        return false;
      }

      errorMessage.value = error.message || labels.value.errors.processFailed;
      return false;
    } finally {
      // A stale request must not clear a newer request's submitting state. (The
      // success path bumps the generation itself via close() → reset(), which
      // also already reset isSubmitting.)
      if (generation === requestGeneration) {
        isSubmitting.value = false;
      }
    }
  }

  return {
    config,
    isOpen,
    openGeneration,
    currentStep,
    inputText,
    terms,
    termFile,
    selectedSpecialtyIds,
    mode,
    enableClientSpecialties,
    previewData,
    isLoading,
    previewToken,
    previewProgress,
    isSubmitting,
    errorMessage,
    completedCount,
    clientLabel,
    specialtyOptions,
    flagToggleLabel,
    clientControlEnabled,
    outcomeLabels,
    statusLabels,
    labels,
    selectableRows,
    assignableRows,
    requestInFlight,
    confirmDisabled,
    progressPercent: progressPercentValue,
    progressText,
    remainingText,
    selectionSummary,
    selectedRowKeys: selection.selectedKeys,
    allRowsSelected: selection.allSelected,
    selectionIndeterminate: selection.indeterminate,
    selectedRowCount: selection.selectedCount,
    isRowSelected: selection.isSelected,
    toggleRow: selection.toggle,
    toggleAllRows: selection.toggleAll,
    configure,
    reset,
    open,
    close,
    addTerms,
    removeTerm,
    commitInput,
    handleFileChange,
    removeFile,
    preview,
    cancelPreview,
    confirm,
  };
});
